import {
    AddPhoneUserForLocation,
    CreatePhoneUserAtLocationDto,
    DeletePhoneUserForLocation,
    GetPhoneUsersForLocation,
    LocationDto,
    PhoneUserAtLocationDto,
} from '../../../store/locationsSlice';
import React, { useEffect, useState } from 'react';
import { usePbDispatch, usePbSelector } from '../../../store/pbStoreHooks';
import LargeCard from '../../main/large-card';
import useTranslate from '../../useTranslate';
import ConfirmButton from '../../util/confirm-button';
import { PbRequest } from '../../../store/pbRequest';
import { RequestStatus } from '../../../store/network/managedRequest';
import Modal from '../../util/modal';
import { Button } from 'react-bootstrap';
import useFeatureAccess from '../../../util/useFeatureAccess';
import LocationPhoneUserAddForm from './location-phone-user-add-form';
import { pbRequestReset } from '../../../store/pbRequestSlice';

interface Props {
    location?: LocationDto;
}

function LocationPhoneUserCard(props: Readonly<Props>) {
    const strings = useTranslate('LocationPhoneUserCard');
    const dispatch = usePbDispatch();
    const phoneUsers = usePbSelector(
        (state) => state.locations.phoneUsersForLocation
    );
    const deleteRequest = usePbSelector(
        (state) => state.pbRequest[PbRequest.DELETE_PHONE_USER_FOR_LOCATION]
    );
    const addRequest = usePbSelector(
        (state) => state.pbRequest[PbRequest.ADD_PHONE_USER_FOR_LOCATION]
    );
    const [addOpen, setAddOpen] = useState(false);
    const selectedPartner = usePbSelector(
        (state) => state.partnerManager.selectedPartner
    );
    const { canWriteFeature } = useFeatureAccess(selectedPartner);
    const mayWritePhoneUser = canWriteFeature('RESIDENTIAL_LOCATION');

    const locationId = props.location?.id ?? 0;

    useEffect(() => {
        dispatch(GetPhoneUsersForLocation(locationId));
    }, [locationId]);
    useEffect(() => {
        if (addRequest.status === RequestStatus.SUCCESS) {
            setAddOpen(false);
        }
    }, [addRequest.status]);

    if (!props.location?.partner_manages_phone_users) {
        return null;
    }

    return <LargeCard title={strings.title}>{renderForm()}</LargeCard>;

    function renderForm() {
        return (
            <>
                <Modal show={addOpen} onClose={handleModalClose}>
                    <LocationPhoneUserAddForm
                        locationId={locationId}
                        onAdd={handleAddPhoneUser}
                        onCancel={handleModalClose}
                        request={addRequest}
                    />
                </Modal>
                <p>{strings.description}</p>
                <table className={'data-table'} style={{ width: '100%' }}>
                    <thead>
                        <tr>
                            <th>{strings.colEmail}</th>
                            <th>{strings.colPhone}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {phoneUsers?.list?.map((user) => (
                            <tr key={user.id}>
                                <td>{user.email}</td>
                                <td>{user.phone}</td>
                                <td>{renderDeleteUserButton(user)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {renderAddButton()}
                {maybeRenderDeleteState()}
            </>
        );
    }

    function renderAddButton() {
        if (!mayWritePhoneUser) {
            return null;
        }
        return (
            <Button
                className={'btn btn-action btn-link'}
                type="button"
                data-testid={'location-phone-user-add-button'}
                onClick={handleModalOpen}
            >
                {strings.buttonAdd}
            </Button>
        );
    }

    function handleModalOpen() {
        dispatch(pbRequestReset(PbRequest.ADD_PHONE_USER_FOR_LOCATION));
        setAddOpen(true);
    }

    function handleAddPhoneUser(phoneUser: CreatePhoneUserAtLocationDto) {
        dispatch(AddPhoneUserForLocation(locationId, phoneUser));
    }

    function handleModalClose() {
        setAddOpen(false);
    }

    function maybeRenderDeleteState() {
        if (deleteRequest.status === RequestStatus.FAILURE) {
            return <div className="alert-danger">{strings.errorRemove}</div>;
        }
        return null;
    }

    function renderDeleteUserButton(phoneUser: PhoneUserAtLocationDto) {
        if (!mayWritePhoneUser) {
            return null;
        }
        return (
            <ConfirmButton
                onConfirmedAction={handleDelete}
                confirmTitle={strings.removeTitle}
                confirmMessage={strings._utils.sprintf(
                    strings.removeDescription,
                    phoneUser.phone
                )}
                confirmAction={strings.removeConfirm}
                rejectAction={strings.removeCancel}
                danger
                linkClassName="icon icon-wew-trash icon-sm"
            >
                <span />
            </ConfirmButton>
        );

        function handleDelete() {
            dispatch(DeletePhoneUserForLocation(locationId, phoneUser.id));
        }
    }
}

export default LocationPhoneUserCard;
